import { apiDocuments } from "@/api/document.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ApiErrorException } from "@/types/api.types.ts";
import {
  ApiDocument,
  ApiDocumentSavePayload,
  ApiDocumentsFilter,
  ApiFolderPayload,
  ApiFolderType,
} from "@/types/document.types.ts";
import { employeeDocumentsKeys } from "@/queries/employees/use-employee-documents.query.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { Control, FieldValues } from "react-hook-form";

export const documentsKeys = createQueryKeys("documents", {
  folders: (folderType: ApiFolderType) => ({
    queryKey: [folderType],
    queryFn: async ({ signal }) => {
      const result = await apiDocuments.getAllFoldersByType(folderType, signal);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  search: (filter: ApiDocumentsFilter) => ({
    queryKey: [filter],
    queryFn: async ({ signal }) => {
      const result = await apiDocuments.searchDocuments(filter, signal);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  detail: (id: number) => ({
    queryKey: [id],
    queryFn: async ({ signal }) => {
      const result = await apiDocuments.getById(id, signal);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data ?? null;
    },
  }),
});

export const useDocumentFoldersList = (folderType: ApiFolderType, enabled = true) => {
  return useQuery({ ...documentsKeys.folders(folderType), enabled });
};

export const useDocument = (id: number, enabled = true) => {
  return useQuery({ ...documentsKeys.detail(id), enabled });
};

interface FolderSaveParams {
  id?: number;
  payload: ApiFolderPayload;
}

export const useDocumentsFolderSave = <T extends FieldValues>(
  control: Control<T> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async ({ id, payload }: FolderSaveParams) => {
      const result = id
        ? await apiDocuments.updateFolder(id, payload)
        : await apiDocuments.createFolder(payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: documentsKeys.folders._def });
      void queryClient.invalidateQueries({ queryKey: employeeDocumentsKeys.folders._def });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useDocumentsFolderDelete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ id }: { id: number }) => {
      const result = await apiDocuments.deleteFolder(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: documentsKeys.folders._def });
      void queryClient.invalidateQueries({ queryKey: employeeDocumentsKeys.folders._def });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useDocumentsSearch = (filter: ApiDocumentsFilter, enabled = true) => {
  return useQuery({
    ...documentsKeys.search(filter),
    enabled: enabled,
  });
};

export const useDocumentDetailRefetcher = () => {
  const queryClient = useQueryClient();

  return useCallback(
    async (documentId: number): Promise<ApiDocument | null> => {
      return (
        (await queryClient.fetchQuery({
          ...documentsKeys.detail(documentId),
          staleTime: 0,
        })) ?? null
      );
    },
    [queryClient]
  );
};

export const useEmployeeDocumentSave = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async ({
      id,
      employeeId,
      payload,
    }: {
      id?: number;
      employeeId: number;
      payload: ApiDocumentSavePayload;
    }) => {
      const result = id
        ? await apiDocuments.updateDocument(id, payload)
        : await apiDocuments.createDocumentForEmployee(employeeId, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: documentsKeys.folders._def });
      void queryClient.invalidateQueries({ queryKey: documentsKeys.search._def });
      void queryClient.invalidateQueries({ queryKey: employeeDocumentsKeys.folders._def });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useOrganizationDocumentSave = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async ({ id, payload }: { id?: number; payload: ApiDocumentSavePayload }) => {
      const result = id
        ? await apiDocuments.updateDocument(id, payload)
        : await apiDocuments.createDocumentForOrganization(payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: documentsKeys.folders._def });
      void queryClient.invalidateQueries({ queryKey: documentsKeys.search._def });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useDocumentDelete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await apiDocuments.deleteDocument(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: documentsKeys.search._def });
      void queryClient.invalidateQueries({ queryKey: employeeDocumentsKeys.folders._def });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};
